import { FileSpreadsheet, Loader2 } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { DataTable } from '@/components/common/DataTable';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useReportExport } from '@/features/product/hooks/useReportExport';
import { formatDisplayDate } from '@/utils/date';
import { formatNumber } from '@/utils/number';
import { donationMembersQueryOptions, exportDonationMembers } from '../api/marketPromotionP2.api';
import { getDonationMemberColumns } from './MpP2Columns';
import { BatchStageBadge } from '@/features/publish/components/BatchStageBadge';
import type { DonationBatch } from '../types/marketPromotionP2.types';

interface DonationMembersDialogProps {
  batch: DonationBatch | null;
  onOpenChange: (open: boolean) => void;
}

export function DonationMembersDialog({ batch, onOpenChange }: DonationMembersDialogProps) {
  const batchId = batch?.batchId;
  const { data, isLoading } = useQuery({
    ...donationMembersQueryOptions(batchId),
    enabled: batchId !== undefined,
  });

  const exporting = useReportExport(() =>
    batchId === undefined ? Promise.resolve() : exportDonationMembers(batchId),
  );

  const rows = data ?? [];
  const total = rows.reduce((sum, row) => sum + row.donationAmount, 0);

  return (
    <Dialog open={batch !== null} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-5xl">
        <div className="min-w-0">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Batch #{batch?.legacyId ?? batch?.batchId}
              {batch === null ? null : <BatchStageBadge stage={batch.stage} />}
            </DialogTitle>
            <DialogDescription>
              {batch === null
                ? ''
                : `${formatNumber(Math.round(batch.promoItemQty))} over ${formatDisplayDate(
                    batch.trnDateFrom ?? '',
                  )} – ${formatDisplayDate(batch.trnDateTo ?? '')}`}
            </DialogDescription>
          </DialogHeader>

          <div className="mt-2 flex items-center justify-between gap-3">
            <p className="text-sm text-muted-foreground">
              {rows.length} member{rows.length === 1 ? '' : 's'} ·{' '}
              <span className="tabular-nums">{formatNumber(Math.round(total))}</span>
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={exporting.run}
              disabled={exporting.isRunning || rows.length === 0}
              className="gap-2"
            >
              {exporting.isRunning ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <FileSpreadsheet className="size-4" />
              )}
              Excel
            </Button>
          </div>

          <div className="mt-3 max-h-[60vh] overflow-auto">
            <DataTable
              data={rows}
              columns={getDonationMemberColumns()}
              isLoading={isLoading}
              emptyMessage="No donation carries this batch's number"
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
